import type {
  AnrufKurz,
  AuswertungAbschnitt,
  Auswertung as Auswertungsdaten,
  AuswertungZeitraum,
  ChannelZeile,
  MitarbeiterZeile,
  Wartezeit,
} from '@nac2s/shared'
import { auswertungAbschnitte, auswertungZeitraeume } from '@nac2s/shared'
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { ButtonGroup, ButtonGroupItem } from '@/components/base/button-group/button-group'
import { Balkendiagramm } from '@/teile/Balkendiagramm'
import { Reiter, Reiterleiste } from '@/teile/Reiter'
import { Status, type Ton } from '@/teile/Status'
import { Fehler, Laedt, Leer, Tabelle } from '@/teile/Tabelle'
import { Zahl } from '@/teile/Zahl'
import { cx } from '@/utils/cx'
import { ladeAuswertung, ladeReihenfolge, speichereReihenfolge } from '../api/auswertung.js'
import { ApiError } from '../api/client.js'
import { Seitenkopf } from '../layout/AppShell.js'
import { useSitzung } from '../session/SitzungContext.js'

/**
 * Anrufauswertung — Edition Pro, für den Light Admin (ADR-0012).
 *
 * Drei Ebenen (Vorgabe des Inhabers, 12.09.2026): **Anlage** — was das ganze
 * Haus betrifft: Erreichbarkeit, Wartezeiten, Wochentage, Tageszeiten,
 * Gesprächsdauern. **Channels** — die Team-Channels mit ihren Kennzahlen.
 * **Mitarbeiter** — wer wie viel telefoniert, wie lange, wann, und was auf
 * seiner persönlichen Nummer einging. Eine persönliche Nummer ist kein
 * Channel.
 */

const ZEITRAEUME: Record<AuswertungZeitraum, string> = {
  woche: 'Diese Woche',
  '30': '30 Tage',
  '90': '90 Tage',
  '180': '180 Tage',
  jahr: 'Dieses Jahr',
  vorjahr: 'Letztes Jahr',
}

const WOCHENTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']

/** Wie der Verschiebegriff die Abschnitte nennt — die Überschriften stehen im Inhalt. */
const ABSCHNITT_TITEL: Record<AuswertungAbschnitt, string> = {
  erreichbarkeit: 'Erreichbarkeit',
  wartezeiten: 'Warten und Aufgeben',
  stosszeiten: 'Stosszeiten',
  heatmap: 'Wochentag und Tageszeit',
  gespraeche: 'Gespräche',
}

type Zustand =
  | { status: 'laedt' }
  | { status: 'da'; daten: Auswertungsdaten }
  | { status: 'fehler'; meldung: string }

export function Auswertung() {
  const { zustand: sitzung, hatRecht } = useSitzung()
  const [zeitraum, setZeitraum] = useState<AuswertungZeitraum>('30')
  const [zustand, setZustand] = useState<Zustand>({ status: 'laedt' })
  const [reihenfolge, setReihenfolge] =
    useState<readonly AuswertungAbschnitt[]>(auswertungAbschnitte)
  const [sortierfehler, setSortierfehler] = useState(false)

  const laden = useCallback(async (z: AuswertungZeitraum) => {
    try {
      setZustand({ status: 'da', daten: await ladeAuswertung(z) })
    } catch (error: unknown) {
      setZustand({
        status: 'fehler',
        meldung: error instanceof ApiError ? error.message : 'Unbekannter Fehler',
      })
    }
  }, [])

  useEffect(() => {
    setZustand({ status: 'laedt' })
    void laden(zeitraum)
  }, [laden, zeitraum])

  useEffect(() => {
    // Lässt sich die gespeicherte Reihenfolge nicht laden, gilt der Standard —
    // die Auswertung selbst bleibt davon unberührt.
    ladeReihenfolge()
      .then((r) => setReihenfolge(r.reihenfolge))
      .catch(() => undefined)
  }, [])

  const verschiebe = useCallback(
    (von: number, nach: number) => {
      if (von === nach || nach < 0 || nach >= reihenfolge.length) return
      const neu = [...reihenfolge]
      const [abschnitt] = neu.splice(von, 1)
      if (!abschnitt) return
      neu.splice(nach, 0, abschnitt)
      setReihenfolge(neu)
      setSortierfehler(false)
      speichereReihenfolge(neu).catch(() => setSortierfehler(true))
    },
    [reihenfolge],
  )

  if (sitzung.status !== 'angemeldet') return null

  const enthalten = sitzung.sitzung.funktionen.includes('anrufauswertung')
  const beisatz = `Edition ${sitzung.sitzung.editionName}`
  if (!enthalten || !hatRecht('auswertung.anrufe')) {
    return (
      <div>
        <Seitenkopf
          beisatz={beisatz}
          titel="Auswertung"
          satz="Erreichbarkeit, Wartezeiten, Stosszeiten — für die Anlage, je Channel und je Mitarbeiter."
        />
        <div className="px-6 pb-8">
          <Leer titel="Nicht verfügbar">
            {enthalten
              ? 'Die Auswertung sieht nur die Selbstverwaltung Ihres Unternehmens.'
              : 'Die Anrufauswertung gehört nicht zu Ihrer Edition. Ihr Partner kann Ihr Unternehmen umstellen.'}
          </Leer>
        </div>
      </div>
    )
  }

  return (
    <div>
      <Seitenkopf
        beisatz={beisatz}
        titel="Auswertung"
        satz="Erreichbarkeit, Wartezeiten, Stosszeiten — für die Anlage, je Channel und je Mitarbeiter."
      />
      <div className="flex flex-col gap-6 px-6 pb-8">
        <ButtonGroup
          size="sm"
          aria-label="Zeitraum"
          selectedKeys={[zeitraum]}
          onSelectionChange={(auswahl) => {
            const [gewaehlt] = [...auswahl]
            if (typeof gewaehlt === 'string' && istZeitraum(gewaehlt)) setZeitraum(gewaehlt)
          }}
        >
          {auswertungZeitraeume.map((z) => (
            <ButtonGroupItem key={z} id={z}>
              {ZEITRAEUME[z]}
            </ButtonGroupItem>
          ))}
        </ButtonGroup>

        {zustand.status === 'laedt' ? (
          <Laedt>Auswertung wird berechnet …</Laedt>
        ) : zustand.status === 'fehler' ? (
          <Fehler>{zustand.meldung}</Fehler>
        ) : (
          <>
            {sortierfehler ? (
              <Fehler>
                Die neue Reihenfolge konnte nicht gespeichert werden — sie gilt nur für diese
                Ansicht. Bitte später erneut verschieben.
              </Fehler>
            ) : null}
            <Ebenen daten={zustand.daten} reihenfolge={reihenfolge} onVerschieben={verschiebe} />
          </>
        )}
      </div>
    </div>
  )
}

function istZeitraum(wert: string): wert is AuswertungZeitraum {
  return (auswertungZeitraeume as readonly string[]).includes(wert)
}

type Ebene = 'anlage' | 'channels' | 'mitarbeiter'

/**
 * Drei Reiter wie in den Kundendetails der Kundenverwaltung. Der Zeitraum
 * steht darüber und gilt für alle drei.
 */
function Ebenen({
  daten,
  reihenfolge,
  onVerschieben,
}: {
  daten: Auswertungsdaten
  reihenfolge: readonly AuswertungAbschnitt[]
  onVerschieben: (von: number, nach: number) => void
}) {
  const [ebene, setEbene] = useState<Ebene>('anlage')
  const a = daten.anlage
  if (a.eingehend.gesamt === 0 && a.ausgehend.gesamt === 0) {
    return (
      <Leer titel="Noch keine Anrufe in diesem Zeitraum">
        Sobald sipgate Anrufe meldet, stehen sie hier. Die Anrufereignisse richtet Ihr Partner ein.
      </Leer>
    )
  }
  return (
    <div className="flex flex-col gap-6">
      <Reiterleiste>
        <Reiter an={ebene === 'anlage'} onWahl={() => setEbene('anlage')}>
          Anlage
        </Reiter>
        <Reiter
          an={ebene === 'channels'}
          onWahl={() => setEbene('channels')}
          anzahl={daten.channels.length}
        >
          Channels
        </Reiter>
        <Reiter
          an={ebene === 'mitarbeiter'}
          onWahl={() => setEbene('mitarbeiter')}
          anzahl={daten.mitarbeiter.length}
        >
          Mitarbeiter
        </Reiter>
      </Reiterleiste>
      <div className="flex flex-col gap-8" role="tabpanel">
        {ebene === 'anlage' ? (
          <Anlage daten={daten} reihenfolge={reihenfolge} onVerschieben={onVerschieben} />
        ) : ebene === 'channels' ? (
          <Channels zeilen={daten.channels} />
        ) : (
          <Mitarbeiter zeilen={daten.mitarbeiter} />
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Ebene 1: Anlage
// ---------------------------------------------------------------------------

/**
 * Fünf Abschnitte, deren Reihenfolge die Person selbst bestimmt — ziehen am
 * Griff oder Pfeiltasten (Auftrag vom 12.09.2026). Gespeichert je Person über
 * `PUT /api/auswertung/reihenfolge`.
 */
function Anlage({
  daten,
  reihenfolge,
  onVerschieben,
}: {
  daten: Auswertungsdaten
  reihenfolge: readonly AuswertungAbschnitt[]
  onVerschieben: (von: number, nach: number) => void
}) {
  const a = daten.anlage
  const e = a.eingehend
  const erreichtAnteil = e.gesamt ? e.vonPersonAngenommen / e.gesamt : 0
  const [gezogen, setGezogen] = useState<AuswertungAbschnitt | null>(null)
  const [ziel, setZiel] = useState<AuswertungAbschnitt | null>(null)

  const abschnitte: Record<AuswertungAbschnitt, ReactNode> = {
    erreichbarkeit: (
      <Abschnitt titel="Erreichbarkeit" satz="Eingehende Anrufe von aussen auf alle Nummern.">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Kachel titel="Anrufe von aussen" wert={e.gesamt} />
          <Kachel
            titel="Von einer Person angenommen"
            wert={e.vonPersonAngenommen}
            beisatz={`${prozent(erreichtAnteil)} der Anrufe`}
            ton="gut"
          />
          <Kachel
            titel="Nicht erreicht"
            wert={e.nichtErreicht}
            beisatz={`davon ${e.anrufbeantworter} beim Anrufbeantworter`}
            ton={e.nichtErreicht > 0 ? 'achtung' : 'neutral'}
          />
          <Kachel
            titel="Wartezeit bis zum Abheben"
            text={dauer(e.wartezeit.medianSek)}
            beisatz={`im Mittel ${dauer(e.wartezeit.durchschnittSek)} · 9 von 10 binnen ${dauer(e.wartezeit.p90Sek)}`}
          />
        </div>
      </Abschnitt>
    ),
    wartezeiten: (
      <div className="grid gap-6 lg:grid-cols-2">
        <Abschnitt
          titel="Wie lange Anrufer warten"
          satz="Bis eine Person abhebt — der Anrufbeantworter zählt nicht."
        >
          <Verteilung wartezeit={e.wartezeit} farbe="text-utility-brand-600" />
        </Abschnitt>
        <Abschnitt
          titel="Wann Anrufer aufgeben"
          satz={`${e.nichtErreicht} Anrufer haben aufgelegt oder den Anrufbeantworter erreicht — im Mittel nach ${dauer(e.abbruch.durchschnittSek)}.`}
        >
          <Verteilung wartezeit={e.abbruch} farbe="text-achtung" />
        </Abschnitt>
      </div>
    ),
    stosszeiten: (
      <div className="grid gap-6 lg:grid-cols-2">
        <Abschnitt titel="Nach Wochentag" satz="Eingehende Anrufe von aussen.">
          <Balkendiagramm
            daten={a.jeWochentag.map((w) => ({
              name: WOCHENTAGE[w.wochentag] ?? '',
              anzahl: w.anzahl,
            }))}
          />
        </Abschnitt>
        <Abschnitt titel="Nach Tageszeit" satz="Stunde des Anrufs, Ortszeit.">
          <Balkendiagramm
            daten={a.jeStunde.map((h) => ({ name: `${h.stunde}`, anzahl: h.anzahl }))}
            beschriftungJede={3}
          />
        </Abschnitt>
      </div>
    ),
    heatmap: (
      <Abschnitt
        titel="Wochentag und Tageszeit"
        satz="Je dunkler, desto mehr Anrufe. So sehen Sie die Stunden, in denen Besetzung zählt."
      >
        <Heatmap zellen={a.heatmap} />
      </Abschnitt>
    ),
    gespraeche: (
      <Abschnitt titel="Gespräche" satz="Zustande gekommene Gespräche, ein- und ausgehend.">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Kachel
            titel="Ausgehende Anrufe"
            wert={a.ausgehend.gesamt}
            beisatz={`${a.ausgehend.zustandeGekommen} zustande gekommen`}
          />
          <Kachel
            titel="Dauer eingehend"
            text={dauer(a.dauer.eingehendSek)}
            beisatz={`${a.dauer.eingehendAnzahl} Gespräche, im Mittel`}
          />
          <Kachel
            titel="Dauer ausgehend"
            text={dauer(a.dauer.ausgehendSek)}
            beisatz={`${a.dauer.ausgehendAnzahl} Gespräche, im Mittel`}
          />
          <Kachel
            titel="Dauer gesamt"
            text={dauer(a.dauer.gesamtSek)}
            beisatz="im Mittel je Gespräch"
          />
        </div>
      </Abschnitt>
    ),
  }

  return (
    <ul
      aria-label="Abschnitte der Anlage — Reihenfolge anpassbar"
      className="flex list-none flex-col gap-8"
    >
      {reihenfolge.map((schluessel, i) => (
        <SortierbarerBereich
          key={schluessel}
          titel={ABSCHNITT_TITEL[schluessel]}
          index={i}
          anzahl={reihenfolge.length}
          wirdGezogen={gezogen === schluessel}
          istZiel={ziel === schluessel && gezogen !== schluessel}
          onVerschieben={onVerschieben}
          onGriff={(zieht) => {
            setGezogen(zieht ? schluessel : null)
            if (!zieht) setZiel(null)
          }}
          onZiel={() => setZiel(schluessel)}
          onZielVerlassen={() => setZiel((z) => (z === schluessel ? null : z))}
          onAblegen={() => {
            if (gezogen) onVerschieben(reihenfolge.indexOf(gezogen), i)
            setGezogen(null)
            setZiel(null)
          }}
        >
          {abschnitte[schluessel]}
        </SortierbarerBereich>
      ))}
    </ul>
  )
}

// ---------------------------------------------------------------------------
// Ebene 2: Channels
// ---------------------------------------------------------------------------

function Channels({ zeilen }: { zeilen: ChannelZeile[] }) {
  const [offen, setOffen] = useState<string | null>(null)
  if (zeilen.length === 0) {
    return (
      <Leer titel="Keine Anrufe auf Team-Channels">
        In diesem Zeitraum lief über keinen Team-Channel ein Anruf. Persönliche Nummern stehen unter
        „Mitarbeiter".
      </Leer>
    )
  }
  return (
    <Abschnitt
      titel="Je Channel"
      satz={
        'Eingehend: was auf der Nummer des Channels ankam. Ausgehend: was über den Channel gewählt wurde. „Mitarbeiter“ zeigt, wer die Gespräche geführt hat.'
      }
    >
      <Tabelle
        kopf={[
          'Channel',
          'Eingehend',
          'Angenommen',
          'Wartezeit (Median)',
          'Nicht erreicht',
          'Aufgegeben nach',
          'Ausgehend',
          'Mitarbeiter',
        ]}
      >
        {zeilen.map((z) => (
          <ChannelZeilen
            key={z.nummer}
            zeile={z}
            offen={offen === z.nummer}
            umschalten={() => setOffen(offen === z.nummer ? null : z.nummer)}
          />
        ))}
      </Tabelle>
    </Abschnitt>
  )
}

function ChannelZeilen({
  zeile: z,
  offen,
  umschalten,
}: {
  zeile: ChannelZeile
  offen: boolean
  umschalten: () => void
}) {
  const meiste = Math.max(1, ...z.mitarbeiter.map((m) => m.angenommen + m.ausgehend))
  return (
    <>
      <tr className={cx('border-b border-secondary', offen && 'border-0')}>
        <td className="py-2.5 pr-2.5">
          <span className="font-medium text-primary">{z.name ?? `+${z.nummer}`}</span>
          {z.name ? (
            <span className="block text-[12px] text-tertiary tabular-nums">+{z.nummer}</span>
          ) : (
            <span className="block text-[12px] text-tertiary">Channel noch nicht aufgelöst</span>
          )}
        </td>
        <td className="py-2.5 pr-2.5 tabular-nums">{z.anrufe}</td>
        <td className="py-2.5 pr-2.5 tabular-nums">
          {z.vonPersonAngenommen}{' '}
          <span className="text-tertiary">
            ({prozent(z.anrufe ? z.vonPersonAngenommen / z.anrufe : 0)})
          </span>
        </td>
        <td className="py-2.5 pr-2.5 tabular-nums">{dauer(z.wartezeitMedianSek)}</td>
        <td className="py-2.5 pr-2.5 tabular-nums">
          {z.nichtErreicht}{' '}
          <span className="text-tertiary">({prozent(z.nichtErreichtAnteil)})</span>
        </td>
        <td className="py-2.5 pr-2.5 tabular-nums">{dauer(z.abbruchDurchschnittSek)}</td>
        <td className="py-2.5 pr-2.5 tabular-nums">
          {z.ausgehend.gesamt}{' '}
          <span className="text-tertiary">({z.ausgehend.zustandeGekommen} erreicht)</span>
        </td>
        <td className="py-2.5">
          <button
            type="button"
            onClick={umschalten}
            aria-expanded={offen}
            className="text-[13px] text-tertiary underline-offset-2 hover:text-primary hover:underline"
          >
            {offen ? 'Schliessen' : `${z.mitarbeiter.length} anzeigen`}
          </button>
        </td>
      </tr>
      {offen ? (
        <tr className="border-b border-secondary">
          <td colSpan={8} className="pb-4">
            <div className="rounded-xl bg-secondary p-4">
              <p className="mb-2 text-[12px] font-medium text-quaternary">
                Verteilung der Gespräche auf die Mitarbeiter
              </p>
              {z.mitarbeiter.length === 0 ? (
                <p className="text-[13px] text-tertiary">
                  Noch kein zustande gekommenes Gespräch auf diesem Channel.
                </p>
              ) : (
                <ul className="flex flex-col divide-y divide-secondary text-[13px]">
                  {z.mitarbeiter.map((m) => (
                    <li key={m.userId} className="flex items-center gap-4 py-1.5">
                      <span className="w-40 shrink-0 font-medium text-primary">{m.name}</span>
                      <div className="flex w-48 items-center gap-2">
                        <span className="w-8 text-right tabular-nums">
                          {m.angenommen + m.ausgehend}
                        </span>
                        <div className="h-2 w-32 overflow-hidden rounded-full bg-utility-neutral-100">
                          <div
                            className={cx(
                              'h-full rounded-full bg-utility-brand-500',
                              BREITEN[breite((m.angenommen + m.ausgehend) / meiste)],
                            )}
                          />
                        </div>
                      </div>
                      <span className="w-36 tabular-nums">{m.angenommen} angenommen</span>
                      <span className="w-32 tabular-nums">{m.ausgehend} ausgehend</span>
                      <span className="w-24 text-right tabular-nums">{dauer(m.dauerSek)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </td>
        </tr>
      ) : null}
    </>
  )
}

// ---------------------------------------------------------------------------
// Ebene 3: Mitarbeiter
// ---------------------------------------------------------------------------

function Mitarbeiter({ zeilen }: { zeilen: MitarbeiterZeile[] }) {
  const [offen, setOffen] = useState<string | null>(null)
  if (zeilen.length === 0) {
    return (
      <Leer titel="Noch keine Gespräche">Kein Mitarbeiter hat in diesem Zeitraum telefoniert.</Leer>
    )
  }
  const meiste = Math.max(1, ...zeilen.map((z) => z.gesamt))
  return (
    <Abschnitt
      titel="Je Mitarbeiter"
      satz={
        'Geführte Gespräche, Gesprächszeit und was auf der persönlichen Nummer einging. „Wann“ zeigt, zu welchen Zeiten jemand telefoniert.'
      }
    >
      <Tabelle
        kopf={[
          'Mitarbeiter',
          'Gespräche',
          'Eingehend',
          'Ausgehend',
          'Gesprächszeit',
          'Persönliche Nummer',
          'Wann',
        ]}
      >
        {zeilen.map((z) => (
          <MitarbeiterZeilen
            key={z.userId || z.name}
            zeile={z}
            meiste={meiste}
            offen={offen === z.userId}
            umschalten={() => setOffen(offen === z.userId ? null : z.userId)}
          />
        ))}
      </Tabelle>
    </Abschnitt>
  )
}

function MitarbeiterZeilen({
  zeile: z,
  meiste,
  offen,
  umschalten,
}: {
  zeile: MitarbeiterZeile
  meiste: number
  offen: boolean
  umschalten: () => void
}) {
  return (
    <>
      <tr className={cx('border-b border-secondary', offen && 'border-0')}>
        <td className="py-2.5 pr-2.5 font-medium text-primary">{z.name}</td>
        <td className="py-2.5 pr-2.5">
          <div className="flex items-center gap-2">
            <span className="w-8 text-right tabular-nums">{z.gesamt}</span>
            <div className="h-2 w-32 max-w-full overflow-hidden rounded-full bg-utility-neutral-100">
              <div
                className={cx(
                  'h-full rounded-full bg-utility-brand-500',
                  BREITEN[breite(z.gesamt / meiste)],
                )}
              />
            </div>
          </div>
        </td>
        <td className="py-2.5 pr-2.5 tabular-nums">{z.eingehend}</td>
        <td className="py-2.5 pr-2.5 tabular-nums">{z.ausgehend}</td>
        <td className="py-2.5 pr-2.5 tabular-nums">{dauer(z.dauerSek)}</td>
        <td className="py-2.5 pr-2.5">
          {z.aufNummer && z.persoenlicheNummer ? (
            <>
              <span className="tabular-nums">
                {z.aufNummer.anrufe} Anrufe, {z.aufNummer.nichtErreicht} nicht erreicht
              </span>
              <span className="block text-[12px] text-tertiary tabular-nums">
                +{z.persoenlicheNummer} · Wartezeit {dauer(z.aufNummer.wartezeitMedianSek)}
              </span>
            </>
          ) : (
            <span className="text-tertiary">–</span>
          )}
        </td>
        <td className="py-2.5">
          <button
            type="button"
            onClick={umschalten}
            aria-expanded={offen}
            className="text-[13px] text-tertiary underline-offset-2 hover:text-primary hover:underline"
          >
            {offen ? 'Schliessen' : 'Anzeigen'}
          </button>
        </td>
      </tr>
      {offen ? (
        <tr className="border-b border-secondary">
          <td colSpan={7} className="pb-4">
            <div className="grid gap-6 rounded-xl bg-secondary p-4 lg:grid-cols-2">
              <div>
                <p className="mb-2 text-[12px] font-medium text-quaternary">
                  Gespräche nach Wochentag
                </p>
                <Balkendiagramm
                  className="h-40"
                  einheit="Gespräche"
                  daten={z.jeWochentag.map((w) => ({
                    name: WOCHENTAGE[w.wochentag] ?? '',
                    anzahl: w.anzahl,
                  }))}
                />
              </div>
              <div>
                <p className="mb-2 text-[12px] font-medium text-quaternary">
                  Gespräche nach Tageszeit
                </p>
                <Balkendiagramm
                  className="h-40"
                  einheit="Gespräche"
                  daten={z.jeStunde.map((h) => ({ name: `${h.stunde}`, anzahl: h.anzahl }))}
                  beschriftungJede={3}
                />
              </div>
              <LetzteAnrufe titel="Letzte eingehende Anrufe" anrufe={z.letzteEingehend} />
              <LetzteAnrufe titel="Letzte ausgehende Anrufe" anrufe={z.letzteAusgehend} />
            </div>
          </td>
        </tr>
      ) : null}
    </>
  )
}

/** Die letzten drei Anrufe eines Mitarbeiters in einer Richtung — mit Ausgang, Zeit und Dauer. */
function LetzteAnrufe({ titel, anrufe }: { titel: string; anrufe: AnrufKurz[] }) {
  return (
    <div>
      <p className="mb-2 text-[12px] font-medium text-quaternary">{titel}</p>
      {anrufe.length === 0 ? (
        <p className="text-[13px] text-tertiary">Keine im Zeitraum.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-secondary text-[13px]">
          {anrufe.map((a) => (
            <li
              key={`${a.beginn}-${a.gegenstelle ?? ''}`}
              className="flex items-center gap-3 py-1.5"
            >
              <span className="w-32 shrink-0 text-tertiary tabular-nums">
                {zeitpunkt(a.beginn)}
              </span>
              <span className="min-w-0 flex-1 truncate tabular-nums">
                {a.gegenstelle ? `+${a.gegenstelle}` : 'unbekannt'}
              </span>
              <Status ton={AUSGANG[a.ausgang].ton}>{AUSGANG[a.ausgang].wort}</Status>
              <span className="w-16 shrink-0 text-right tabular-nums">
                {a.dauerSek === null ? '–' : dauer(a.dauerSek)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** Was der Anwender liest — und in welcher Farbe der Lage. */
const AUSGANG: Record<AnrufKurz['ausgang'], { wort: string; ton: Ton }> = {
  angenommen: { wort: 'angenommen', ton: 'gut' },
  zustande_gekommen: { wort: 'erreicht', ton: 'gut' },
  anrufbeantworter: { wort: 'Anrufbeantworter', ton: 'wartet' },
  abgebrochen: { wort: 'abgebrochen', ton: 'ruhig' },
  nicht_erreicht: { wort: 'nicht erreicht', ton: 'stoerung' },
  offen: { wort: 'läuft', ton: 'wartet' },
}

function zeitpunkt(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// ---------------------------------------------------------------------------
// Bausteine
// ---------------------------------------------------------------------------

function Abschnitt({
  titel,
  satz,
  children,
}: {
  titel: string
  satz: string
  children: ReactNode
}) {
  return (
    <section className="flex flex-col gap-4">
      <div>
        <h2 className="font-display text-base font-bold text-primary">{titel}</h2>
        <p className="text-[13px] text-tertiary">{satz}</p>
      </div>
      {children}
    </section>
  )
}

/**
 * Ein verschiebbarer Abschnitt der Anlagen-Ansicht. Gezogen wird am Griff
 * oben rechts (HTML-Drag-and-Drop, ohne Zusatzbibliothek); wer nicht ziehen
 * kann oder will, verschiebt mit den Pfeiltasten auf dem Griff.
 */
function SortierbarerBereich({
  titel,
  index,
  anzahl,
  wirdGezogen,
  istZiel,
  onVerschieben,
  onGriff,
  onZiel,
  onZielVerlassen,
  onAblegen,
  children,
}: {
  titel: string
  index: number
  anzahl: number
  wirdGezogen: boolean
  istZiel: boolean
  onVerschieben: (von: number, nach: number) => void
  onGriff: (zieht: boolean) => void
  onZiel: () => void
  onZielVerlassen: () => void
  onAblegen: () => void
  children: ReactNode
}) {
  const bereich = useRef<HTMLLIElement>(null)
  return (
    <li
      ref={bereich}
      className={cx(
        'group/sortierbar relative rounded-xl',
        wirdGezogen && 'opacity-40',
        istZiel && 'outline-2 outline-offset-8 outline-utility-brand-300',
      )}
      onDragOver={(ev) => {
        ev.preventDefault()
        ev.dataTransfer.dropEffect = 'move'
        onZiel()
      }}
      onDragLeave={onZielVerlassen}
      onDrop={(ev) => {
        ev.preventDefault()
        onAblegen()
      }}
    >
      <button
        type="button"
        draggable
        aria-label={`„${titel}“ verschieben — ziehen oder Pfeiltaste hoch/runter`}
        title="Verschieben: ziehen oder Pfeiltaste hoch/runter"
        onDragStart={(ev) => {
          ev.dataTransfer.effectAllowed = 'move'
          // Firefox startet ohne Daten keinen Drag.
          ev.dataTransfer.setData('text/plain', titel)
          if (bereich.current) ev.dataTransfer.setDragImage(bereich.current, 24, 24)
          onGriff(true)
        }}
        onDragEnd={() => onGriff(false)}
        onKeyDown={(ev) => {
          if (ev.key === 'ArrowUp' && index > 0) {
            ev.preventDefault()
            onVerschieben(index, index - 1)
          }
          if (ev.key === 'ArrowDown' && index < anzahl - 1) {
            ev.preventDefault()
            onVerschieben(index, index + 1)
          }
        }}
        className="absolute -top-1 right-0 cursor-grab rounded-md p-1.5 text-quaternary opacity-0 outline-brand transition-opacity hover:bg-secondary hover:text-primary focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-2 active:cursor-grabbing group-hover/sortierbar:opacity-100"
      >
        {/* Sechs Punkte — der übliche Griff. */}
        <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" className="size-4">
          <circle cx="6" cy="3.5" r="1.3" />
          <circle cx="10" cy="3.5" r="1.3" />
          <circle cx="6" cy="8" r="1.3" />
          <circle cx="10" cy="8" r="1.3" />
          <circle cx="6" cy="12.5" r="1.3" />
          <circle cx="10" cy="12.5" r="1.3" />
        </svg>
      </button>
      {children}
    </li>
  )
}

const KACHELTON = {
  neutral: 'text-primary',
  gut: 'text-fg-success-primary',
  // Bernstein heisst „da müssen Sie ran" — Rot bleibt Grenzen vorbehalten.
  achtung: 'text-achtung-tief',
} as const

function Kachel({
  titel,
  wert,
  text,
  beisatz,
  ton = 'neutral',
}: {
  titel: string
  wert?: number
  text?: string
  beisatz?: string
  ton?: keyof typeof KACHELTON
}) {
  return (
    <div className="rounded-xl border border-secondary px-4 py-3.5">
      <p className="text-[12px] font-medium text-quaternary">{titel}</p>
      <p
        className={cx(
          'mt-1.5 font-display text-[1.9rem] leading-none font-extrabold tracking-[-0.02em]',
          KACHELTON[ton],
        )}
      >
        {wert !== undefined ? <Zahl wert={wert} /> : text}
      </p>
      {beisatz ? <p className="mt-1.5 text-[12px] text-tertiary">{beisatz}</p> : null}
    </div>
  )
}

function Verteilung({ wartezeit, farbe }: { wartezeit: Wartezeit; farbe: string }) {
  if (wartezeit.anzahl === 0) {
    return <p className="text-sm text-tertiary">Keine Anrufe in dieser Gruppe.</p>
  }
  return (
    <div className="flex flex-col gap-3">
      <Balkendiagramm
        daten={wartezeit.verteilung.map((k) => ({ name: k.klasse, anzahl: k.anzahl }))}
        farbe={farbe}
      />
      <p className="text-[12px] text-tertiary">
        Median {dauer(wartezeit.medianSek)} · Mittel {dauer(wartezeit.durchschnittSek)} · 9 von 10
        binnen {dauer(wartezeit.p90Sek)} · {wartezeit.anzahl} Anrufe
      </p>
    </div>
  )
}

/** Sechs Stufen, aus den Utility-Farben — je dunkler, desto mehr Anrufe. */
const STUFEN = [
  'bg-utility-neutral-50',
  'bg-utility-brand-100',
  'bg-utility-brand-200',
  'bg-utility-brand-300',
  'bg-utility-brand-500',
  'bg-utility-brand-700',
] as const

function Heatmap({ zellen }: { zellen: Auswertungsdaten['anlage']['heatmap'] }) {
  const werte = new Map(zellen.map((z) => [`${z.wochentag}:${z.stunde}`, z.anzahl]))
  const hoechster = Math.max(0, ...zellen.map((z) => z.anzahl))
  const stufe = (anzahl: number): string => {
    if (anzahl === 0 || hoechster === 0) return STUFEN[0]
    const i = Math.min(5, Math.max(1, Math.ceil((anzahl / hoechster) * 5)))
    return STUFEN[i] ?? STUFEN[5]
  }
  const stunden = Array.from({ length: 24 }, (_, i) => i)
  return (
    <div className="overflow-x-auto">
      <div className="grid min-w-[40rem] grid-cols-[2.5rem_repeat(24,minmax(0,1fr))] gap-1 text-[11px] text-quaternary">
        <div />
        {stunden.map((h) => (
          <div key={h} className="text-center tabular-nums">
            {h % 3 === 0 ? h : ''}
          </div>
        ))}
        {WOCHENTAGE.map((tag, w) => (
          <Reihe key={tag} tag={tag}>
            {stunden.map((h) => {
              const anzahl = werte.get(`${w}:${h}`) ?? 0
              return (
                <div
                  key={h}
                  title={`${tag} ${h}:00 – ${anzahl} Anrufe`}
                  className={cx('aspect-square rounded-[3px]', stufe(anzahl))}
                />
              )
            })}
          </Reihe>
        ))}
      </div>
      <p className="mt-2 text-[12px] text-tertiary">
        Stunden 0 bis 23, Ortszeit. Dunkelste Zelle: {hoechster} Anrufe.
      </p>
    </div>
  )
}

function Reihe({ tag, children }: { tag: string; children: ReactNode }) {
  return (
    <>
      <div className="flex items-center font-medium text-tertiary">{tag}</div>
      {children}
    </>
  )
}

/** Elf Breiten statt eines Inline-Stils (TECHNOLOGY.md: keine Inline-Styles). */
const BREITEN = [
  'w-[4%]',
  'w-[10%]',
  'w-[20%]',
  'w-[30%]',
  'w-[40%]',
  'w-[50%]',
  'w-[60%]',
  'w-[70%]',
  'w-[80%]',
  'w-[90%]',
  'w-full',
] as const

function breite(anteil: number): number {
  return Math.min(10, Math.max(0, Math.round(anteil * 10)))
}

function prozent(anteil: number): string {
  return `${Math.round(anteil * 100)} %`
}

/** Sekunden lesbar: „12 s", „1:05 min", „1:12 h". */
function dauer(sekunden: number | null): string {
  if (sekunden === null) return '–'
  const ganz = Math.round(sekunden)
  if (ganz < 60) return `${ganz} s`
  const min = Math.floor(ganz / 60)
  const rest = ganz % 60
  if (min < 60) return `${min}:${String(rest).padStart(2, '0')} min`
  const h = Math.floor(min / 60)
  return `${h}:${String(min % 60).padStart(2, '0')} h`
}
